import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import {
  FaLayerGroup,
  FaArrowUp,
  FaArrowDown,
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import "./GroupSummary.css";
import { TableRowsSkeleton } from "../../Component/Skeleton";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable already applied or fallback
}

const formatINR = (val) => {
  const num = Number(val || 0);
  if (!num || num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function GroupSummary() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const BASE_API_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000/api";

  const companyId = useMemo(() => {
    return (
      selectedCompany?.CompID ||
      selectedCompany?.id ||
      sessionStorage.getItem("CompID") ||
      sessionStorage.getItem("companyId") ||
      "1147"
    );
  }, [selectedCompany]);

  const companyInfo = useMemo(() => {
    return {
      name: selectedCompany?.Name || selectedCompany?.name || selectedCompany?.CompanyName || "DAZY TOURE TRAVEL'S",
      address: selectedCompany?.Address || selectedCompany?.address || "Opp. GPO, M.I. Road, Jaipur-302001",
      gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "",
      stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
    };
  }, [selectedCompany]);

  const todayStr = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);

  // Restore state from sessionStorage so navigating back does not reset the page
  const savedState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("group_summary_state");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const [asOnDate, setAsOnDate] = useState(() => savedState?.asOnDate || todayStr);
  const [balanceFilter, setBalanceFilter] = useState(() => savedState?.balanceFilter || "non-zero");
  const [searchTerm, setSearchTerm] = useState(() => savedState?.searchTerm || "");
  const [currentPage, setCurrentPage] = useState(() => savedState?.currentPage || 1);
  const [rowsPerPage, setRowsPerPage] = useState(() => savedState?.rowsPerPage || 25);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Save state on every change
  useEffect(() => {
    sessionStorage.setItem(
      "group_summary_state",
      JSON.stringify({
        asOnDate,
        balanceFilter,
        searchTerm,
        currentPage,
        rowsPerPage,
      })
    );
  }, [asOnDate, balanceFilter, searchTerm, currentPage, rowsPerPage]);

  // Live timestamp for "As on" badge
  const asOnTime = useMemo(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const mNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = mNames[now.getMonth()];
    const year = now.getFullYear();
    const hours = now.getHours();
    const mins = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${day}-${mon}-${year} ${h12}:${mins} ${ampm}`;
  }, []);

  // Fetch All Groups Summary
  const fetchAllGroups = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_API_URL}/reports/all-groups-summary/${companyId}`, {
        params: { asOnDate },
      });
      if (res.data?.success) {
        setGroups(res.data.data || []);
      } else {
        setGroups([]);
        showToast.info("No group records found");
      }
    } catch (err) {
      console.error("Failed to load group summary:", err);
      // Fallback try ledger endpoint prefix
      try {
        const fallbackRes = await axios.get(`${BASE_API_URL}/reports/ledger/all-groups-summary/${companyId}`, {
          params: { asOnDate },
        });
        if (fallbackRes.data?.success) {
          setGroups(fallbackRes.data.data || []);
        }
      } catch (e2) {
        showToast.error("Failed to load group summary");
      }
    } finally {
      setLoading(false);
    }
  }, [BASE_API_URL, companyId, asOnDate]);

  useEffect(() => {
    if (companyId) {
      fetchAllGroups();
    }
  }, [companyId, fetchAllGroups]);

  const handleReset = () => {
    sessionStorage.removeItem("group_summary_state");
    setAsOnDate(todayStr);
    setBalanceFilter("non-zero");
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Filter groups by balance and search term
  const filteredGroups = useMemo(() => {
    return groups.filter((g) => {
      // Balance filter: 'non-zero' excludes groups where debit and credit are both 0
      if (balanceFilter === "non-zero") {
        const debit = Number(g.debit || 0);
        const credit = Number(g.credit || 0);
        if (Math.abs(debit) <= 0.0001 && Math.abs(credit) <= 0.0001) {
          return false;
        }
      }
      // Search term filter
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        if (!g.groupName || !g.groupName.toLowerCase().includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [groups, searchTerm, balanceFilter]);

  // Paginated records
  const totalPages = Math.ceil(filteredGroups.length / rowsPerPage) || 1;
  const paginatedGroups = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredGroups.slice(start, start + rowsPerPage);
  }, [filteredGroups, currentPage, rowsPerPage]);

  // Sum of Debit and Credit for filtered groups
  const totals = useMemo(() => {
    let totDebit = 0;
    let totCredit = 0;
    filteredGroups.forEach((g) => {
      totDebit += Number(g.debit || 0);
      totCredit += Number(g.credit || 0);
    });
    return {
      debit: Math.round(totDebit * 100) / 100,
      credit: Math.round(totCredit * 100) / 100,
    };
  }, [filteredGroups]);

  // Navigate to Group Ledger Summary when a group is clicked
  const handleGroupClick = (groupCode, groupName) => {
    navigate(
      `/group-ledger-summary?groupCode=${groupCode}&groupName=${encodeURIComponent(groupName)}&asOnDate=${asOnDate}&filter=${balanceFilter}`
    );
  };

  // Export to Excel
  const handleExportExcel = () => {
    try {
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "GROUP SUMMARY";
      const filterLabel = balanceFilter === "non-zero" ? "Non Zero" : "All";
      const periodSubtitle = `As on Date: ${asOnDate}  |  Filter: ${filterLabel}  |  Generated: ${asOnTime}`;

      const headers = ["S.No.", "Group Name", "Debit (INR)", "Credit (INR)"];

      const dataRows = filteredGroups.map((g, idx) => [
        idx + 1,
        g.groupName,
        g.debit > 0 ? g.debit : "-",
        g.credit > 0 ? g.credit : "-",
      ]);

      const totalRow = [
        "Total",
        "",
        totals.debit,
        totals.credit,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        ...dataRows,
        totalRow,
        [],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 3 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 3 } },
      ];

      worksheet["!cols"] = [
        { wch: 8 },  // S.No
        { wch: 45 }, // Group Name
        { wch: 22 }, // Debit
        { wch: 22 }, // Credit
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Group_Summary");
      XLSX.writeFile(workbook, `Group_Summary_${asOnDate}.xlsx`);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export Excel");
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [
        companyInfo.address,
        companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
        companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
      ]
        .filter(Boolean)
        .join("   |   ");
      const reportTitle = "GROUP SUMMARY";

      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });

      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      const filterLabel = balanceFilter === "non-zero" ? "Non Zero" : "All";
      doc.text(`As on Date: ${asOnDate}   |   Filter: ${filterLabel}`, 14, 36);
      doc.text(`Generated: ${asOnTime}   |   Groups: ${filteredGroups.length}`, pageWidth - 14, 36, {
        align: "right",
      });

      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      const tableRows = [
        ...filteredGroups.map((g, idx) => [
          idx + 1,
          g.groupName,
          g.debit > 0 ? formatINR(g.debit) : "-",
          g.credit > 0 ? formatINR(g.credit) : "-",
        ]),
        [
          "",
          "Total",
          formatINR(totals.debit),
          formatINR(totals.credit),
        ],
      ];

      autoTable(doc, {
        head: [
          [
            { content: "S.No.", styles: { halign: "center" } },
            { content: "Group Name", styles: { halign: "left" } },
            { content: "Debit (INR)", styles: { halign: "right" } },
            { content: "Credit (INR)", styles: { halign: "right" } },
          ],
        ],
        body: tableRows,
        startY: 42,
        theme: "plain",
        styles: {
          fontSize: 8.5,
          cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
          textColor: [0, 0, 0],
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fontStyle: "bold",
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: { top: 0.5, bottom: 0.5 },
        },
        columnStyles: {
          0: { cellWidth: 15, halign: "center" },
          1: { cellWidth: "auto", halign: "left" },
          2: { cellWidth: 38, halign: "right" },
          3: { cellWidth: 38, halign: "right" },
        },
        didParseCell: (data) => {
          if (data.row.index === tableRows.length - 1) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.lineWidth = { top: 0.5, bottom: 0.5 };
            data.cell.styles.lineColor = [0, 0, 0];
          }
        },
        margin: { left: 14, right: 14, bottom: 18 },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageCurrent = data.pageNumber;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(100, 100, 100);
          doc.text("etour - Design & Develop by Response Infoway", 14, 290);
          doc.text(`Page ${pageCurrent} of ${pageCount}`, pageWidth - 14, 290, { align: "right" });
        },
      });

      doc.save(`Group_Summary_${asOnDate}.pdf`);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export PDF");
    }
  };

  // Dedicated Print handler
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="gs_page_wrapper">
      {/* Breadcrumb Bar */}
      <div className="gs_breadcrumb_bar">
        <div>
          <Link to="/" style={{ color: "#64748b", textDecoration: "none" }}>
            Reports
          </Link>
          <span>&gt;</span>
          <span style={{ color: "#1e3a8a", fontWeight: 700 }}>Group Summary</span>
        </div>

        <div className="gs_as_on_badge">
          <FaClock />
          <span>As on: {asOnTime}</span>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="gs_filter_bar">
        <div className="gs_filter_left">
          <div className="gs_filter_group">
            <label className="gs_filter_label">
              As On Date<span className="req">*</span>:
            </label>
            <input
              type="date"
              className="gs_filter_input"
              value={asOnDate}
              onChange={(e) => {
                setAsOnDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="gs_filter_group">
            <span className="gs_filter_label">Filter:</span>
            <div className="gs_toggle_group">
              <button
                type="button"
                className={`gs_toggle_btn ${balanceFilter === "all" ? "active" : ""}`}
                onClick={() => {
                  setBalanceFilter("all");
                  setCurrentPage(1);
                }}
              >
                All
              </button>
              <button
                type="button"
                className={`gs_toggle_btn ${balanceFilter === "non-zero" ? "active" : ""}`}
                onClick={() => {
                  setBalanceFilter("non-zero");
                  setCurrentPage(1);
                }}
              >
                Non Zero
              </button>
            </div>
          </div>

          <button type="button" className="gs_btn_view" onClick={fetchAllGroups}>
            View
          </button>
          <button type="button" className="gs_btn_reset" onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>

      {/* KPI Cards Row (3 Cards) */}
      <div className="gs_kpi_row">
        <div className="gs_kpi_card">
          <div className="gs_kpi_icon_box gs_icon_groups">
            <FaLayerGroup />
          </div>
          <div className="gs_kpi_content">
            <span className="gs_kpi_label">TOTAL GROUPS</span>
            <span className="gs_kpi_value">{filteredGroups.length}</span>
          </div>
        </div>

        <div className="gs_kpi_card">
          <div className="gs_kpi_icon_box gs_icon_debit">
            <FaArrowUp />
          </div>
          <div className="gs_kpi_content">
            <span className="gs_kpi_label">TOTAL DEBIT</span>
            <span className="gs_kpi_value">
              ₹ {formatINR(totals.debit)}{" "}
              <span className="drcr_text_dr">Dr</span>
            </span>
          </div>
        </div>

        <div className="gs_kpi_card">
          <div className="gs_kpi_icon_box gs_icon_credit">
            <FaArrowDown />
          </div>
          <div className="gs_kpi_content">
            <span className="gs_kpi_label">TOTAL CREDIT</span>
            <span className="gs_kpi_value">
              ₹ {formatINR(totals.credit)}{" "}
              <span className="drcr_text_cr">Cr</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="gs_table_card">
        {/* Table Toolbar */}
        <div className="gs_table_toolbar">
          <h3 className="gs_table_title">Account Groups Summary</h3>

          <div className="gs_toolbar_right">
            {/* Search Box */}
            <div className="gs_search_box">
              <FaSearch className="gs_search_icon" />
              <input
                type="text"
                className="gs_search_input"
                placeholder="Search group name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Action Buttons */}
            <button
              type="button"
              className="gs_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export to Microsoft Excel"
            >
              <FaFileExcel />
              Export Excel
            </button>
            <button
              type="button"
              className="gs_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export to PDF"
            >
              <FaFilePdf />
              Export PDF
            </button>
            <button
              type="button"
              className="gs_export_btn btn_print"
              onClick={handlePrint}
              title="Print Summary"
            >
              <FaPrint />
              Print
            </button>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="gs_table_wrapper">
          <table className="gs_custom_table">
            <thead>
              <tr>
                <th style={{ width: "60px", textAlign: "center" }}>S.No.</th>
                <th>Group Name</th>
                <th style={{ textAlign: "right", width: "170px" }}>Debit (₹)</th>
                <th style={{ textAlign: "right", width: "170px" }}>Credit (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableRowsSkeleton columns={4} rows={5} />
              ) : filteredGroups.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: "center", padding: "20px", color: "#a0aec0" }}>
                    No groups found.
                  </td>
                </tr>
              ) : (
                paginatedGroups.map((row, idx) => (
                  <tr
                    key={row.groupCode || idx}
                    onClick={() => handleGroupClick(row.groupCode, row.groupName)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ textAlign: "center", color: "#718096" }}>
                      {(currentPage - 1) * rowsPerPage + idx + 1}
                    </td>
                    <td>
                      <span
                        className="gs_group_link"
                        title="Click to view ledgers in this group"
                      >
                        {row.groupName}
                      </span>
                    </td>
                    <td className="gs_table_num">
                      {row.debit > 0 ? formatINR(row.debit) : ""}
                    </td>
                    <td className="gs_table_num">
                      {row.credit > 0 ? formatINR(row.credit) : ""}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {filteredGroups.length > 0 && (
              <tfoot>
                <tr className="gs_table_total_row">
                  <td style={{ textAlign: "center" }}></td>
                  <td>Total</td>
                  <td className="gs_table_num">{formatINR(totals.debit)}</td>
                  <td className="gs_table_num">{formatINR(totals.credit)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination Row */}
        {filteredGroups.length > 0 && (
          <div className="gs_pagination_row">
            <div>
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, filteredGroups.length)} of{" "}
              {filteredGroups.length} groups
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="gs_pagination_btns">
                <button
                  type="button"
                  className="gs_page_nav_btn"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  title="Previous Page"
                >
                  <FaChevronLeft size={10} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => {
                  if (
                    pg === 1 ||
                    pg === totalPages ||
                    (pg >= currentPage - 2 && pg <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pg}
                        type="button"
                        className={`gs_page_num_btn ${currentPage === pg ? "active" : ""}`}
                        onClick={() => setCurrentPage(pg)}
                      >
                        {pg}
                      </button>
                    );
                  }
                  if (pg === currentPage - 3 || pg === currentPage + 3) {
                    return (
                      <span key={pg} style={{ padding: "0 4px", color: "#a0aec0" }}>
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                <button
                  type="button"
                  className="gs_page_nav_btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  title="Next Page"
                >
                  <FaChevronRight size={10} />
                </button>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span>Rows per page:</span>
                <select
                  className="gs_rows_select"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* DEDICATED PRINT CONTAINER (Hidden on screen, Active during @media print) */}
      <div className="gs_print_area d-none">
        <div style={{ textAlign: "center", borderBottom: "2px solid #000000", paddingBottom: "8px", marginBottom: "10px" }}>
          <h2 style={{ margin: "0 0 3px 0", fontSize: "19px", fontWeight: "bold", textTransform: "uppercase", color: "#000000" }}>
            {companyInfo.name}
          </h2>
          <div style={{ fontSize: "11px", color: "#222222", lineHeight: 1.4 }}>
            {companyInfo.address && `${companyInfo.address}  |  `}
            <strong>GSTIN:</strong> {companyInfo.gstin}
            {companyInfo.stateCode && `  |  State Code: ${companyInfo.stateCode}`}
          </div>
          <div style={{ marginTop: "6px", fontSize: "13px", fontWeight: "bold", color: "#000000" }}>
            GROUP SUMMARY
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "8px", borderBottom: "1px solid #000000", paddingBottom: "4px" }}>
          <div>
            <strong>As on Date:</strong> {asOnDate}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Date:</strong> {asOnTime}  |  <strong>Groups:</strong> {filteredGroups.length}
          </div>
        </div>

        <table className="gs_print_table">
          <thead>
            <tr>
              <th style={{ width: "8%", textAlign: "center" }}>S.No.</th>
              <th style={{ width: "52%" }}>Group Name</th>
              <th style={{ width: "20%", textAlign: "right" }}>Debit (₹)</th>
              <th style={{ width: "20%", textAlign: "right" }}>Credit (₹)</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map((row, idx) => (
              <tr key={row.groupCode || idx} className={idx % 2 === 1 ? "print_row_alt" : ""}>
                <td style={{ textAlign: "center" }}>{idx + 1}</td>
                <td style={{ fontWeight: 500 }}>{row.groupName}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {row.debit > 0 ? formatINR(row.debit) : "-"}
                </td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {row.credit > 0 ? formatINR(row.credit) : "-"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="print_total_row">
              <td style={{ textAlign: "center" }}></td>
              <td style={{ fontWeight: "bold" }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>{formatINR(totals.debit)}</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>{formatINR(totals.credit)}</td>
            </tr>
          </tfoot>
        </table>

        <div style={{ marginTop: "12px", display: "flex", justifyContent: "space-between", fontSize: "10px", color: "#444" }}>
          <span>etour - Design & Develop by Response Infoway</span>
          <span>Printed on: {asOnTime}</span>
        </div>
      </div>
    </div>
  );
}
